import { createSelector } from "reselect";
import { DictionaryParametersTypes } from "../global/global.actions";
import { metricTemplatesByIdSelector, metricTemplatesSelector, parametersDictionarySelector, } from "../global/global.selectors";
import { MetricTypes, } from "../global/global.types";
import { ScenarioEntityType, EngineResultType, PsGlobalEntityType, } from "./ps-workspace.types";
import { ResultSetElementType, SCENARIO__UPDATE_WORKSPACE_SOCKET_METHODS, RESULT_SET__UPDATE_WORKSPACE_SOCKET_METHODS, ATTACHED_METRIC__UPDATE_WORKSPACE_SOCKET_METHODS, VALUES__UPDATE_WORKSPACE_SOCKET_METHODS, CONFIGURATION__UPDATE_WORKSPACE_SOCKET_METHODS, METRIC__UPDATE_WORKSPACE_SOCKET_METHODS, UPDATE_WORKSPACE_SOCKET_METHODS, } from "./ps-workspace.update.types";
import { prepareKnownValues, getAttachmentsAvailabilityForRelativeMetric, MAX_ENTITIES_COUNT, getResultSetElementsCount, } from "./ps-workspace.utils";
import { noActiveLicensesSelector } from "../licensing/licensing.selectors";
import { compareResultItemIds } from "utils/data";
import { PSWorkspaceLabels } from "shared/labels";
const resultElementHumanTypes = {
    [EngineResultType.Result0D]: "Tabular view",
    [EngineResultType.Result1D]: "Line graph",
    [EngineResultType.Result2D]: "Heatmap",
    [EngineResultType.ResultMetadata]: "Metadata table",
};
const getResultElementSummaryData = (element, resultSet) => ({
    elementType: element.type,
    elementHumanType: resultElementHumanTypes[element.type],
    elementId: element.id,
    elementName: element.name,
    resultSetId: resultSet.id,
    resultSetName: resultSet.name,
});
export const getWorkspaceStateSelector = (state) => state.psWorkspace;
export const workspaceSavingStatusSelector = createSelector(getWorkspaceStateSelector, (state) => state.saving);
export const workspaceSelector = createSelector(getWorkspaceStateSelector, (state) => state.workspace);
export const isSubscribedSelector = createSelector(getWorkspaceStateSelector, (state) => state.isSubscribed);
export const workspaceIdSelector = createSelector(getWorkspaceStateSelector, (state) => state.id);
export const socketStatusSelector = createSelector(getWorkspaceStateSelector, (state) => state.socketStatus);
export const pendingSocketOperationByRequestIdSelector = (requestId) => createSelector(getWorkspaceStateSelector, (state) => state.pendingSocketOperations.find((operation) => operation.requestId === requestId));
export const duplicationInProgressSelector = createSelector(getWorkspaceStateSelector, (state) => {
    if (state.pendingSocketOperations.length === 0) {
        return false;
    }
    const methods = [
        SCENARIO__UPDATE_WORKSPACE_SOCKET_METHODS.DuplicateAnalysis,
        RESULT_SET__UPDATE_WORKSPACE_SOCKET_METHODS.DuplicateResultSet,
        RESULT_SET__UPDATE_WORKSPACE_SOCKET_METHODS.DuplicateResultSetElement,
    ];
    return state.pendingSocketOperations.some((operation) => methods.includes(operation.method));
});
export const updatingTargetInRelativeAttachmentSelector = createSelector(getWorkspaceStateSelector, (state) => state.pendingSocketOperations.some((operation) => operation.method === ATTACHED_METRIC__UPDATE_WORKSPACE_SOCKET_METHODS.UpdateTargetInRelativeAttachment));
export const updatingSourceInRelativeAttachmentSelector = createSelector(getWorkspaceStateSelector, (state) => state.pendingSocketOperations.some((operation) => operation.method === ATTACHED_METRIC__UPDATE_WORKSPACE_SOCKET_METHODS.UpdateSourceInRelativeAttachment));
export const removeKnownValueInProgressSelector = createSelector(getWorkspaceStateSelector, (state) => state.pendingSocketOperations.some((operation) => operation.method === VALUES__UPDATE_WORKSPACE_SOCKET_METHODS.RemoveKnownValue));
export const addScenarioInProgressSelector = createSelector(getWorkspaceStateSelector, (state) => state.pendingSocketOperations.some((operation) => operation.method === UPDATE_WORKSPACE_SOCKET_METHODS.AddAnalysis));
export const addResultSetInProgressSelector = createSelector(getWorkspaceStateSelector, (state) => state.pendingSocketOperations.some((operation) => operation.method === UPDATE_WORKSPACE_SOCKET_METHODS.AddResultSet));
export const internalEntityAddInProgressSelector = createSelector(getWorkspaceStateSelector, (state) => {
    if (state.pendingSocketOperations.length === 0) {
        return false;
    }
    const methods = [
        CONFIGURATION__UPDATE_WORKSPACE_SOCKET_METHODS.AddConfiguration,
        METRIC__UPDATE_WORKSPACE_SOCKET_METHODS.AddAbsoluteRiskProfileToAnalysis,
        METRIC__UPDATE_WORKSPACE_SOCKET_METHODS.AddRelativeRiskProfileToAnalysis,
    ];
    return state.pendingSocketOperations.some((operation) => methods.includes(operation.method));
});
export const socketLastErrorsSelector = createSelector(getWorkspaceStateSelector, (state) => state.lastSocketErrors);
export const workspaceNameSelector = createSelector(getWorkspaceStateSelector, (state) => state.name || "");
export const renameWorkspaceInProgressSelector = createSelector(getWorkspaceStateSelector, (state) => state.renameWorkspaceInProgress);
export const exportResultsInProgressSelector = createSelector(getWorkspaceStateSelector, (state) => state.exportInProgress);
export const scenarioExportInProgressSelector = createSelector(getWorkspaceStateSelector, (state) => state.scenarioExportInProgress);
export const workspaceDescriptionSelector = createSelector(getWorkspaceStateSelector, (state) => state.description || null);
export const workspaceTagsSelector = createSelector(getWorkspaceStateSelector, (state) => state.tags || []);
export const workspaceTagsSavingSelector = createSelector(getWorkspaceStateSelector, (state) => state.tagsSaving || false);
export const workspaceResultSetsSelector = createSelector(workspaceSelector, (state) => state?.resultSets || []);
export const currentScenarioIdSelector = createSelector(getWorkspaceStateSelector, (state) => state.currentScenarioId);
export const currentScenarioEntitySelector = createSelector(getWorkspaceStateSelector, (state) => state.currentScenarioEntity);
export const currentResultsSetIdSelector = createSelector(getWorkspaceStateSelector, (state) => state.currentResultSetId);
export const currentResultSetElementSelector = createSelector(getWorkspaceStateSelector, (state) => state.currentResultSetElement);
export const configurationElementPathSelector = createSelector(currentScenarioIdSelector, currentScenarioEntitySelector, (analysisId, currentScenarioEntity) => ({
    analysisId,
    configurationId: currentScenarioEntity !== null ? currentScenarioEntity.id : null,
}));
export const selectedResultsSetSelector = createSelector(currentResultsSetIdSelector, workspaceSelector, (resultSetId, workspace) => resultSetId === null
    ? {}
    : workspace?.resultSets?.find(({ id }) => id === resultSetId) || {});
export const isEmptyResultSetSelector = createSelector(selectedResultsSetSelector, (resultSet) => {
    const { resultElements: { tableElements = [], lineGraphElements = [], heatMapElements = [], metadataElements = [], } = {}, } = resultSet;
    return (!tableElements.length && !lineGraphElements.length && !heatMapElements.length && !metadataElements.length);
});
export const selectedScenarioSelector = createSelector(currentScenarioIdSelector, workspaceSelector, (scenarioId, workspace) => scenarioId === null
    ? {}
    : workspace?.analyses?.find(({ id }) => id === scenarioId) || {});
export const workspaceScenariosSelector = createSelector(workspaceSelector, (workspace) => workspace?.analyses || []);
export const currentScenarioEntityNameSelector = createSelector(selectedScenarioSelector, currentScenarioEntitySelector, (scenario, currentScenarioEntity) => {
    if (currentScenarioEntity === null) {
        return "";
    }
    switch (currentScenarioEntity.type) {
        case ScenarioEntityType.Configuration:
            return (scenario?.configurations?.find(({ id }) => id === currentScenarioEntity.id)?.name ||
                `Configuration ${currentScenarioEntity.id}`);
        case ScenarioEntityType.SimpleMetric:
            return (scenario?.absoluteRiskProfiles?.find(({ id }) => id === currentScenarioEntity.id)?.name ||
                `Simple Metric ${currentScenarioEntity.id}`);
        case ScenarioEntityType.RelativeMetric:
            return (scenario?.relativeRiskProfiles?.find(({ id }) => id === currentScenarioEntity.id)?.name ||
                `Relative Metric ${currentScenarioEntity.id}`);
        default:
            return "";
    }
});
export const selectedConfigurationSelector = createSelector(currentScenarioEntitySelector, selectedScenarioSelector, (currentEntity, scenario) => currentEntity !== null && scenario !== null && currentEntity.type === ScenarioEntityType.Configuration
    ? scenario?.configurations?.find(({ id }) => id === currentEntity.id) || {}
    : {});
export const preparedKnownValuesSelector = (knownValues) => createSelector(parametersDictionarySelector(DictionaryParametersTypes.KnownValue), (parametersDictionary) => prepareKnownValues(knownValues, parametersDictionary));
export const workspaceLayoutSelector = createSelector(getWorkspaceStateSelector, (workspace) => workspace.layout || []);
export const selectedResultSetLayout = createSelector(selectedResultsSetSelector, getWorkspaceStateSelector, (resultSet, workspace) => workspace.layout?.find((item) => item.id === resultSet.id));
export const orderedResultsSetElementsSelector = createSelector(currentResultsSetIdSelector, workspaceSelector, selectedResultSetLayout, (resultSetId, workspace, layout) => {
    if (resultSetId === null) {
        return [];
    }
    const tableElements = [];
    const lineGraphElements = [];
    const heatMapElements = [];
    const metadataElements = [];
    const resultSet = workspace?.resultSets?.find(({ id }) => id === resultSetId) || {};
    if (resultSet.resultElements) {
        resultSet.resultElements.tableElements.forEach((element) => {
            const elementLayout = layout?.resultSetElements?.find((item) => item.type === ResultSetElementType.TabularView && item.elementIndex === element.id);
            tableElements.push({
                type: ResultSetElementType.TabularView,
                element,
                order: elementLayout?.order || "",
                elementIndex: elementLayout?.elementIndex,
            });
        });
        resultSet.resultElements.lineGraphElements.forEach((element) => {
            const elementLayout = layout?.resultSetElements?.find((item) => item.type === ResultSetElementType.LineGraph && item.elementIndex === element.id);
            lineGraphElements.push({
                type: ResultSetElementType.LineGraph,
                element,
                order: elementLayout?.order || "",
                elementIndex: elementLayout?.elementIndex,
            });
        });
        resultSet.resultElements.heatMapElements.forEach((element) => {
            const elementLayout = layout?.resultSetElements?.find((item) => item.type === ResultSetElementType.HeatMap && item.elementIndex === element.id);
            heatMapElements.push({
                type: ResultSetElementType.HeatMap,
                element,
                order: elementLayout?.order || "",
                elementIndex: elementLayout?.elementIndex,
            });
        });
        resultSet.resultElements.metadataElements.forEach((element) => {
            const elementLayout = layout?.resultSetElements?.find((item) => item.type === ResultSetElementType.Metadata && item.elementIndex === element.id);
            metadataElements.push({
                type: ResultSetElementType.Metadata,
                element,
                order: elementLayout?.order || "",
                elementIndex: elementLayout?.elementIndex,
            });
        });
    }
    return [...tableElements, ...lineGraphElements, ...heatMapElements, ...metadataElements];
});
export const dividedResultSetElementsLayoutSelector = (showTwoColumns) => createSelector(orderedResultsSetElementsSelector, (data) => {
    const leftColumn = [];
    const rightColumn = [];
    const sortByOrder = (a, b) => {
        const [, indexA] = a.order.split(".");
        const [, indexB] = b.order.split(".");
        return parseInt(indexA) - parseInt(indexB);
    };
    const leftData = data.filter((item) => {
        const [side] = item.order.split(".");
        return side === "left";
    });
    const rightData = data.filter((item) => {
        const [side] = item.order.split(".");
        return side === "right";
    });
    if (showTwoColumns) {
        leftData.forEach(({ element, type, order, elementIndex }, index) => {
            leftColumn[index] = { element, type, order, elementIndex };
        });
        rightData.forEach(({ element, type, order, elementIndex }, index) => {
            rightColumn[index] = { element, type, order, elementIndex };
        });
        return {
            left: {
                items: leftColumn.length === 0
                    ? null
                    : leftColumn
                        .slice()
                        .sort(sortByOrder)
                        .map((item, i) => ({ ...item, staticOrder: i + 1 })),
            },
            right: {
                items: rightColumn
                    .slice()
                    .sort(sortByOrder)
                    .map((item, i) => ({ ...item, staticOrder: i + 1 + leftColumn.length })),
            },
        };
    }
    else {
        const data = [...leftData.slice().sort(sortByOrder), ...rightData.slice().sort(sortByOrder)];
        data.forEach(({ element, type, order, elementIndex }) => {
            const [, index] = order.split(".");
            leftColumn.push({ element, type, order, index, elementIndex });
        });
        return {
            left: {
                items: leftColumn.length === 0
                    ? null
                    : leftColumn.slice().map(({ element, type, order, elementIndex }, i) => ({
                        element,
                        type,
                        order,
                        elementIndex,
                        staticOrder: i + 1,
                    })),
            },
        };
    }
});
export const resultSetTableElementConfigurationOrdersSelector = (tableElementId) => createSelector(selectedResultsSetSelector, getWorkspaceStateSelector, (resultSet, workspace) => {
    const layout = workspace.layout?.find((item) => item.id === resultSet.id);
    const tableElementsLayout = layout?.tableElements?.find((item) => parseInt(item.id) === tableElementId);
    return tableElementsLayout ? tableElementsLayout.configurations : [];
});
export const resultSetMetadataElementConfigurationOrdersSelector = (tableElementId) => createSelector(selectedResultsSetSelector, getWorkspaceStateSelector, (resultSet, workspace) => {
    const layout = workspace.layout?.find((item) => item.id === resultSet.id);
    const metadataElementsLayout = layout?.metadataElements?.find((item) => parseInt(item.id) === tableElementId);
    return metadataElementsLayout ? metadataElementsLayout.configurations : [];
});
export const orderedConfigurationTableElementsSelector = (tableElementId) => createSelector(resultSetTableElementConfigurationOrdersSelector(tableElementId), selectedResultsSetSelector, (tableElementsOrders, resultSet) => {
    const { resultElements: { tableElements }, } = resultSet;
    const targetTableElement = tableElements.find((item) => item.id === tableElementId);
    return tableElementsOrders
        .map(({ configurationId, analysisId, order }) => {
        const element = targetTableElement?.configurations.find((item) => item.analysisId === analysisId && item.configurationId === configurationId);
        return {
            order,
            element: configurationId === null && analysisId === null
                ? {
                    id: element?.id || 0,
                    analysis: "",
                    analysisId: null,
                    configuration: "",
                    composedName: "",
                    configurationId: null,
                }
                : element,
        };
    })
        .sort((a, b) => parseInt(a.order) - parseInt(b.order));
});
export const orderedConfigurationMetadataElementsSelector = (metadataElementId) => createSelector(resultSetMetadataElementConfigurationOrdersSelector(metadataElementId), selectedResultsSetSelector, (metadataElementsOrders, resultSet) => {
    const { resultElements: { metadataElements }, } = resultSet;
    const metadataTableElement = metadataElements.find((item) => item.id === metadataElementId);
    return metadataElementsOrders
        .map(({ configurationId, analysisId, order }) => {
        const element = metadataTableElement?.configurations.find((item) => item.analysisId === analysisId && item.configurationId === configurationId);
        return {
            order,
            element: configurationId === null && analysisId === null
                ? {
                    id: element?.id || 0,
                    analysis: "",
                    analysisId: null,
                    configuration: "",
                    composedName: "",
                    configurationId: null,
                }
                : element,
        };
    })
        .sort((a, b) => parseInt(a.order) - parseInt(b.order));
});
export const orderedParameterTableElementsSelector = (tableElementId) => createSelector(resultSetTableElementParameterOrdersSelector(tableElementId), selectedResultsSetSelector, (tableElementsOrders, resultSet) => {
    const { resultElements: { tableElements }, } = resultSet;
    const targetTableElement = tableElements.find((item) => item.id === tableElementId);
    return tableElementsOrders
        .map(({ parameterId, unitsId, order }) => {
        const isNewEntity = parameterId === "new" || unitsId === "new";
        const element = targetTableElement?.parameters.find((item) => item.parameterId === parameterId && item.unitsId === unitsId);
        return {
            order,
            element: isNewEntity
                ? {
                    parameterId: parameterId === "new" ? "new" : parameterId,
                    unitsId: unitsId === "new" ? unitsId : "",
                }
                : element,
        };
    })
        .sort((a, b) => parseInt(a.order) - parseInt(b.order));
});
export const resultSetTableElementParameterOrdersSelector = (tableElementId) => createSelector(selectedResultsSetSelector, getWorkspaceStateSelector, (resultSet, workspace) => {
    const layout = workspace.layout?.find((item) => item.id === resultSet.id);
    const tableElementsLayout = layout?.tableElements?.find((item) => parseInt(item.id) === tableElementId);
    return tableElementsLayout ? tableElementsLayout.parameters : [];
});
export const currentResultSetElementDataSelector = createSelector(selectedResultsSetSelector, currentResultSetElementSelector, (resultSet, currentResultSetElement) => {
    if (resultSet === null || currentResultSetElement === null) {
        return null;
    }
    switch (currentResultSetElement.type) {
        case EngineResultType.Result0D:
            return (resultSet.resultElements.tableElements.find(({ id }) => id === currentResultSetElement.id) ||
                {});
        case EngineResultType.Result1D:
            return (resultSet.resultElements.lineGraphElements.find(({ id }) => id === currentResultSetElement.id) ||
                {});
        case EngineResultType.Result2D:
            return (resultSet.resultElements.heatMapElements.find(({ id }) => id === currentResultSetElement.id) ||
                {});
        case EngineResultType.ResultMetadata:
            return (resultSet.resultElements.metadataElements.find(({ id }) => id === currentResultSetElement.id) ||
                {});
        default:
            return null;
    }
});
export const configurationsTreeSelector = createSelector(getWorkspaceStateSelector, (state) => state.workspace?.analyses.reduce((acc, { id, name, configurations }) => ({
    ...acc,
    [id]: {
        name,
        configurations: configurations.reduce((acc2, configuration) => ({ ...acc2, [configuration.id]: configuration.name }), {}),
    },
}), {}) || {});
export const lineGraphElementExportDataSelector = (resultSetId, resultSetElementId) => createSelector(getWorkspaceStateSelector, configurationsTreeSelector, parametersDictionarySelector(DictionaryParametersTypes.ResultIndependent), parametersDictionarySelector(DictionaryParametersTypes.ResultDependent), (state, configurationsTree, xDictionaryParameters, yDictionaryParameters) => {
    const resultSet = state.workspace?.resultSets.find(({ id }) => id === resultSetId) || {};
    const element = resultSet.resultElements.lineGraphElements.find(({ id }) => id === resultSetElementId) ||
        {};
    const { name: xParameter, units } = xDictionaryParameters[element.xAxis.parameterId] || {};
    const { name: xUnit } = (units || []).find(({ id }) => id === element.xAxis.unitsId) || {};
    const responses = element.responses.map((response) => ({
        id: response.id,
        analysisId: response.analysisId,
        analysis: response.analysisId !== null ? configurationsTree[response.analysisId].name : "",
        xConfigurationId: response.xConfigurationId,
        xConfiguration: response.analysisId !== null && response.xConfigurationId !== null
            ? configurationsTree[response.analysisId].configurations[response.xConfigurationId]
            : "",
        yConfigurationId: response.yConfigurationId,
        yConfiguration: response.analysisId !== null && response.yConfigurationId !== null
            ? configurationsTree[response.analysisId].configurations[response.yConfigurationId]
            : "",
        yParameterId: response.yParameterId,
        yParameter: yDictionaryParameters[response.yParameterId]?.name || "",
        yUnitsId: response.yUnitsId,
        yUnits: ((yDictionaryParameters[response.yParameterId]?.units || []).find(({ id }) => id === response.yUnitsId) || {}).name || "",
    }));
    return { xParameter, xUnit, responses };
});
export const heatmapElementExportDataSelector = (resultSetId, resultSetElementId) => createSelector(getWorkspaceStateSelector, configurationsTreeSelector, parametersDictionarySelector(DictionaryParametersTypes.ResultIndependent), parametersDictionarySelector(DictionaryParametersTypes.ResultDependent), (state, configurationsTree, xDictionaryParameters, yzDictionaryParameters) => {
    const resultSet = state.workspace?.resultSets.find(({ id }) => id === resultSetId) || {};
    const element = resultSet.resultElements.heatMapElements.find(({ id }) => id === resultSetElementId) ||
        {};
    const { name: xParameter, units: xUnits } = xDictionaryParameters[element.xAxis.parameterId] || {};
    const { name: xUnit } = (xUnits || []).find(({ id }) => id === element.xAxis.unitsId) || {};
    const { name: yParameter, units: yUnits } = yzDictionaryParameters[element.yAxis.parameterId] || {};
    const { name: yUnit } = (yUnits || []).find(({ id }) => id === element.yAxis.unitsId) || {};
    const responses = element.responses.map((response) => {
        const { name: zParameter, units: zUnits } = yzDictionaryParameters[response.zParameterId] || {};
        const { name: zUnit } = (zUnits || []).find(({ id }) => id === response.zUnitsId) || {};
        return {
            id: response.id,
            analysisId: response.analysisId,
            analysis: response.analysisId !== null ? configurationsTree[response.analysisId].name : "",
            xConfigurationId: response.xConfigurationId,
            xConfiguration: response.analysisId !== null &&
                response.xConfigurationId !== null &&
                response.xConfigurationId !== 0
                ? configurationsTree[response.analysisId].configurations[response.xConfigurationId]
                : "",
            yConfigurationId: response.yConfigurationId,
            yConfiguration: response.analysisId !== null &&
                response.yConfigurationId !== null &&
                response.yConfigurationId !== 0
                ? configurationsTree[response.analysisId].configurations[response.yConfigurationId]
                : "",
            zConfigurationId: response.yConfigurationId,
            zConfiguration: response.analysisId !== null &&
                response.zConfigurationId !== null &&
                response.zConfigurationId !== 0
                ? configurationsTree[response.analysisId].configurations[response.zConfigurationId]
                : "",
            zParameterId: response.zParameterId,
            zParameter,
            zUnit,
        };
    });
    return { xParameter, xUnit, yParameter, yUnit, responses };
});
export const resultSetElementStaticOrderSelector = createSelector(currentResultSetElementDataSelector, dividedResultSetElementsLayoutSelector(true), (currentResultSetElement, orderedResultSetElements) => {
    const items = [
        ...(orderedResultSetElements.left.items || []),
        ...(orderedResultSetElements.right?.items || []),
    ];
    const layout = items.find((item) => item.element.id === currentResultSetElement?.id && item.element.type === currentResultSetElement.type);
    return layout?.staticOrder || 1;
});
export const tabularResultSetElementByIdSelector = (resultSetId, resultSetElementId) => createSelector(getWorkspaceStateSelector, (workspace) => {
    const resultSet = workspace.workspace?.resultSets.find(({ id }) => resultSetId === id) || {};
    return (resultSet.resultElements.tableElements.find(({ id }) => id === resultSetElementId) ||
        {});
});
export const metadataResultSetElementByIdSelector = (resultSetId, resultSetElementId) => createSelector(getWorkspaceStateSelector, (workspace) => {
    const resultSet = workspace.workspace?.resultSets.find(({ id }) => resultSetId === id) || {};
    return (resultSet.resultElements.metadataElements.find(({ id }) => id === resultSetElementId) ||
        {});
});
export const metricTemplateParamSpecsSelector = (paramId, type, id, templateId) => createSelector(selectedScenarioSelector, metricTemplatesByIdSelector(paramId, type), (scenario, templates) => {
    if (!templateId) {
        return [];
    }
    const riskProfiles = type === "simple" ? scenario?.absoluteRiskProfiles : scenario?.relativeRiskProfiles;
    const template = templates.find(({ id }) => id === templateId);
    const paramSpecs = template?.paramSpecs ?? [];
    const matchingProfile = riskProfiles?.find((profile) => profile.id === id && profile.templateId === templateId);
    const profileTemplateParameters = matchingProfile?.profileTemplateParameters ?? [];
    return profileTemplateParameters.length
        ? paramSpecs.map((spec) => {
            const templateParameter = profileTemplateParameters.find((parameter) => parameter.name === spec.name);
            return {
                ...spec,
                minimumValue: templateParameter?.minimumValue === undefined ? null : templateParameter?.minimumValue,
                maximumValue: templateParameter?.maximumValue === undefined ? null : templateParameter.maximumValue,
                value: templateParameter?.value,
                valueSpecified: templateParameter?.valueSpecified,
            };
        })
        : [];
});
export const metricRowDataSelector = (type, attachedRisk) => createSelector(parametersDictionarySelector(DictionaryParametersTypes.AbsoluteTemplate), parametersDictionarySelector(DictionaryParametersTypes.RelativeTemplate), metricTemplatesSelector, (absoluteParameters, relativeParameters, metricTemplates) => {
    const parameters = type === "relative" ? relativeParameters : absoluteParameters;
    return {
        ...attachedRisk,
        parameter: (attachedRisk.paramId && parameters?.[attachedRisk.paramId]) || {},
        template: (attachedRisk.templateId && metricTemplates?.[attachedRisk.templateId]) ||
            {},
    };
});
export const metricsListSelector = (analysisId, type) => createSelector(workspaceSelector, parametersDictionarySelector(DictionaryParametersTypes.AbsoluteTemplate), parametersDictionarySelector(DictionaryParametersTypes.RelativeTemplate), metricTemplatesSelector, (workspace, absoluteParameters, relativeParameters, metricTemplates) => {
    const analyse = workspace?.analyses.find(({ id }) => id === analysisId);
    const parameters = type === "relative" ? relativeParameters : absoluteParameters;
    return (((type === "relative" ? analyse?.relativeRiskProfiles : analyse?.absoluteRiskProfiles) || []).map((profile) => ({
        ...profile,
        units: profile?.unitsId,
        parameter: (profile?.paramId && parameters[profile?.paramId]) || {},
        template: (profile?.templateId && metricTemplates[profile?.templateId]) || {},
        type,
    })) || []);
});
export const activeMetricDataSelector = createSelector(selectedScenarioSelector, currentScenarioEntitySelector, parametersDictionarySelector(DictionaryParametersTypes.AbsoluteTemplate), parametersDictionarySelector(DictionaryParametersTypes.RelativeTemplate), metricTemplatesSelector, (scenario, currentEntity, absoluteParameters, relativeParameters, templates) => {
    if (currentEntity === null ||
        scenario === null ||
        currentEntity.type === ScenarioEntityType.Configuration) {
        return {};
    }
    const type = currentEntity.type === ScenarioEntityType.SimpleMetric ? MetricTypes.simple : MetricTypes.relative;
    const profile = (type === "relative" ? scenario?.relativeRiskProfiles : scenario?.absoluteRiskProfiles)?.find(({ id }) => id === currentEntity.id) || {};
    const parameters = currentEntity.type === ScenarioEntityType.SimpleMetric ? absoluteParameters : relativeParameters;
    return {
        ...profile,
        units: profile?.unitsId,
        parameter: (profile?.paramId && parameters[profile.paramId]) || {},
        template: (profile?.templateId && templates[profile.templateId]) || {},
        type,
    };
});
export const templateAnnotationsGraphDataSelector = createSelector(getWorkspaceStateSelector, (state) => state.templateAnnotationsGraphResults);
export const serverErrorSelector = createSelector(getWorkspaceStateSelector, (state) => state.serverError);
export const serverWarningSelector = createSelector(getWorkspaceStateSelector, (state) => state.serverWarning);
export const tabularViewLayoutSelector = (resultSetId, resultSetElementId) => createSelector(workspaceSelector, tabularResultSetElementByIdSelector(resultSetId, resultSetElementId), workspaceLayoutSelector, parametersDictionarySelector(DictionaryParametersTypes.Result0D), (workspace, element, layout, parametersDictionary) => {
    const { configurations = [], parameters = [] } = layout
        .find(({ id }) => resultSetId === id)
        ?.tableElements.find(({ id }) => parseInt(id) === resultSetElementId) || {};
    return [...configurations]
        .sort((configuration1, configuration2) => parseInt(configuration1?.order) - parseInt(configuration2?.order))
        .reduce((rowsAccumulator, row) => {
        if (row.analysisId !== null && row.configurationId !== null) {
            const { composedName: configuration, analysis, id, } = element.configurations.find(({ configurationId, analysisId }) => configurationId === row.configurationId && analysisId === row.analysisId) || {};
            const orderedParameters = [...parameters]
                .sort((parameter1, parameter2) => parseInt(parameter1.order) - parseInt(parameter2.order))
                .reduce((columnsAccumulator, { order, parameterId, unitsId }) => {
                if (parameterId !== "new" && unitsId !== "new") {
                    const { name: parameterName, units = [] } = parametersDictionary[parameterId] || {};
                    const { name: unitName } = units.find(({ id }) => id === unitsId) || {};
                    const composedId = `${parameterId}-${unitsId}`;
                    columnsAccumulator.push({
                        name: parameterName,
                        subtitle: unitName,
                        order,
                        composedId,
                        primaryId: parameterId,
                        secondaryId: unitsId,
                    });
                }
                return columnsAccumulator;
            }, []);
            const subtitle = workspace !== null && workspace.analyses.length > 1 ? analysis : null;
            rowsAccumulator.push({
                id,
                rowName: configuration,
                analysis,
                subtitle,
                columns: orderedParameters,
            });
        }
        return rowsAccumulator;
    }, []);
});
export const metadataLayoutSelector = (resultSetId, resultSetElementId) => createSelector(workspaceSelector, metadataResultSetElementByIdSelector(resultSetId, resultSetElementId), calculationResultsMetadataSelector(resultSetId, resultSetElementId), workspaceLayoutSelector, (workspace, element, calculationResults, layout) => {
    const valuesMap = {};
    calculationResults.forEach(({ recordId, data: { attributes, data } }) => {
        attributes.forEach((attribute, atrIndex) => {
            valuesMap[attribute.name] = [
                ...(valuesMap[attribute.name] || []),
                { recordId, data: data[atrIndex], description: attribute.value },
            ];
        });
    });
    const { configurations = [] } = layout
        .find(({ id }) => resultSetId === id)
        ?.metadataElements.find(({ id }) => parseInt(id) === resultSetElementId) || {};
    return Object.keys(valuesMap).map((rowName, rowIndex) => {
        const columns = [...configurations]
            .sort((config1, config2) => parseInt(config1.order) - parseInt(config2.order))
            .reduce((columnsAccumulator, { order, analysisId, configurationId }) => {
            if (analysisId !== null && configurationId !== null) {
                const columnConfiguration = element.configurations.find((el) => el.analysisId === analysisId && el.configurationId === configurationId);
                const name = columnConfiguration?.configuration || "";
                const analysis = columnConfiguration?.analysis || "";
                const { data = [] } = valuesMap[rowName]?.find((item) => item.recordId === columnConfiguration?.id) || {};
                const subtitle = workspace !== null && workspace.analyses.length > 1 ? analysis || "" : null;
                columnsAccumulator.push({ name, order, subtitle, data });
            }
            return columnsAccumulator;
        }, []);
        const rowDescription = valuesMap[rowName][0] ? valuesMap[rowName][0].description : "";
        return { id: rowIndex, rowName, rowDescription, columns };
    });
});
export const calculationResultsTabularSelector = (resultSetId, resultSetElementId) => createSelector(getWorkspaceStateSelector, (state) => {
    const calculationTabularResults = state.calculationTabularResults.filter((item) => compareResultItemIds(item, { resultSetId, resultSetElementId }));
    return (calculationTabularResults || [
        {
            data: [],
            parameters: [],
            resultSetElementId,
            resultSetId,
            type: EngineResultType.Result0D,
            recordId: null,
        },
    ]);
});
export const calculationResultsLineGraphSelector = (resultSetId, resultSetElementId) => createSelector(getWorkspaceStateSelector, (state) => state.calculationLineGraphResults.find((item) => compareResultItemIds(item, { resultSetId, resultSetElementId })) || {});
export const calculationResultsHeatmapSelector = (resultSetId, resultSetElementId) => createSelector(getWorkspaceStateSelector, (state) => state.calculationHeatmapResults.find((item) => compareResultItemIds(item, { resultSetId, resultSetElementId })) || {});
export const calculationResultsMetadataSelector = (resultSetId, resultSetElementId) => createSelector(getWorkspaceStateSelector, (state) => {
    const calculationMetadataResults = state.calculationMetadataResults.filter((item) => compareResultItemIds(item, { resultSetId, resultSetElementId }));
    return (calculationMetadataResults || [
        {
            resultSetId,
            resultSetElementId,
            type: EngineResultType.ResultMetadata,
            recordId: null,
            data: {},
        },
    ]);
});
export const scenarioEntityCreatingSelector = createSelector(getWorkspaceStateSelector, (state) => state.scenarioEntityCreating);
export const resultEntityCreatingSelector = createSelector(getWorkspaceStateSelector, (state) => state.resultEntityCreating);
export const printCanvasContentSelector = createSelector(getWorkspaceStateSelector, (state) => state.printCanvasContent);
export const allScenarioConfigurationsOptionsSelector = createSelector(selectedScenarioSelector, (scenario) => scenario !== null ? scenario.configurations.map(({ name, id }) => ({ value: id, label: name })) : []);
export const relativeProfileAttachmentsAvailabilitySelector = (id) => createSelector(selectedScenarioSelector, (scenario) => {
    if (scenario === null) {
        return {};
    }
    return getAttachmentsAvailabilityForRelativeMetric(scenario, id);
});
export const isMetricProfilesListEmptySelector = (scenarioId) => createSelector(workspaceSelector, (workspace) => {
    const scenario = workspace?.analyses.find(({ id }) => id === scenarioId);
    return !scenario?.absoluteRiskProfiles.length && !scenario?.relativeRiskProfiles.length;
});
export const allAttachableMetricProfilesListSelector = (scenarioId, configurationId) => createSelector(workspaceSelector, (workspace) => {
    const scenario = workspace?.analyses.find(({ id }) => id === scenarioId);
    const simpleMetricProfiles = scenario?.absoluteRiskProfiles.filter((profile) => {
        const attachedConfigurations = profile.configurations?.map(({ targetId }) => targetId) || [];
        return configurationId === null || !attachedConfigurations.includes(configurationId);
    }) || [];
    const relativeMetricProfiles = scenario?.relativeRiskProfiles.filter((profile) => {
        const { availableSources = [] } = getAttachmentsAvailabilityForRelativeMetric(scenario, profile.id);
        return availableSources.length > 0;
    }) || [];
    return [
        ...simpleMetricProfiles.map((profile) => ({ ...profile, type: "simple" })),
        ...relativeMetricProfiles.map((profile) => ({ ...profile, type: "relative" })),
    ];
});
export const deleteTargetSelector = createSelector(getWorkspaceStateSelector, (state) => state.deleteTarget);
export const onePanelSelector = createSelector(getWorkspaceStateSelector, (state) => state.onePanelView);
export const panelForceExpandedSelector = createSelector(getWorkspaceStateSelector, (state) => state.panelForceExpanded);
export const persistPanelsStateSelector = createSelector(getWorkspaceStateSelector, (state) => state.shouldPersistPanelsState);
export const duplicatedResultSetIdSelector = createSelector(getWorkspaceStateSelector, (state) => state.duplicatedResultSetId);
export const protectedSimpleMetricSelector = createSelector(getWorkspaceStateSelector, (state) => state.protectedSimpleMetricAttachments);
export const configurationIdWithHighlightedElementsSelector = createSelector(getWorkspaceStateSelector, (state) => state.configurationIdWithHighlightedElements);
export const metricIdWithHighlightedElementsSelector = createSelector(getWorkspaceStateSelector, (state) => state.metricIdWithHighlightedElements);
export const metricTypeWithHighlightedElementsSelector = createSelector(getWorkspaceStateSelector, (state) => state.metricTypeWithHighlightedElements);
export const isMetricHighlightedInTreeSelector = (metric) => createSelector(configurationIdWithHighlightedElementsSelector, (configurationIdWithHighlightedElements) => {
    if (!metric || configurationIdWithHighlightedElements === null || !metric.configurations) {
        return false;
    }
    if (metric.type === MetricTypes.simple) {
        return metric.configurations.some((item) => item.targetId === configurationIdWithHighlightedElements);
    }
    else {
        return metric.configurations.some((item) => item.sourceId === configurationIdWithHighlightedElements ||
            item.targetId === configurationIdWithHighlightedElements);
    }
});
export const isConfigurationHighlightedAsSiblingInTreeSelector = (configuration) => createSelector(configurationIdWithHighlightedElementsSelector, (configurationIdWithHighlightedElements) => {
    if (!configuration || (!configuration.attachedRelativeRisks && !configuration.attachedAbsoluteRisks)) {
        return false;
    }
    const hasMatchInSimpleMetrics = configuration.attachedAbsoluteRisks.some((risk) => {
        return risk.configurations.some((item) => item.targetId === configurationIdWithHighlightedElements);
    });
    const hasMatchInRelativeMetrics = configuration.attachedRelativeRisks.some((risk) => {
        return risk.configurations.some((item) => item.sourceId === configurationIdWithHighlightedElements ||
            item.targetId === configurationIdWithHighlightedElements);
    });
    return (configurationIdWithHighlightedElements !== null &&
        (hasMatchInSimpleMetrics || hasMatchInRelativeMetrics));
});
export const isConfigurationHighlightedAsAttachedInTreeSelector = (configuration) => createSelector(metricIdWithHighlightedElementsSelector, metricTypeWithHighlightedElementsSelector, (metricIdWithHighlightedElements, metricTypeWithHighlightedElements) => {
    if (!configuration ||
        metricIdWithHighlightedElements === null ||
        metricTypeWithHighlightedElements === null) {
        return false;
    }
    const hasMatchInSimpleMetrics = configuration.attachedAbsoluteRisks.some((risk) => {
        return risk.configurations.some((item) => item.riskId === metricIdWithHighlightedElements &&
            metricTypeWithHighlightedElements === MetricTypes.simple);
    });
    const hasMatchInRelativeMetrics = configuration.attachedRelativeRisks.some((risk) => {
        return risk.configurations.some((item) => item.riskId === metricIdWithHighlightedElements &&
            metricTypeWithHighlightedElements === MetricTypes.relative);
    });
    return hasMatchInSimpleMetrics || hasMatchInRelativeMetrics;
});
export const showOverlaySelector = (http, isHome) => createSelector(isSubscribedSelector, serverErrorSelector, socketLastErrorsSelector, noActiveLicensesSelector, (isSubscribed, serverError, socketLastErrors, noActiveLicenses) => {
    if (http) {
        return !isHome && noActiveLicenses;
    }
    else {
        return !isSubscribed && (http ? serverError : socketLastErrors) === null;
    }
});
export const errorDataSelector = (http) => createSelector(serverErrorSelector, socketLastErrorsSelector, (serverError, socketLastErrors) => {
    const error = http ? (serverError === null ? null : serverError.data) : socketLastErrors;
    if (error !== null) {
        const { errors = [] } = error;
        const { title, text, errorType, reportMail } = { ...errors[0] };
        return {
            message: text,
            title: title || errorType || "Error",
            errorType,
            reportMail,
        };
    }
    return null;
});
export const resultElementsWithLongCalculationSelector = createSelector(getWorkspaceStateSelector, (workspace) => workspace.resultElementsWithLongCalculation || []);
export const longCalculationResultElementSelector = (type, resultSetId, resultSetElementId) => createSelector(getWorkspaceStateSelector, (workspace) => workspace.resultElementsWithLongCalculation.find((item) => type === item.type &&
    resultSetId === item.resultSetId &&
    resultSetElementId === item.resultSetElementId));
export const maxScenariosReachedSelector = createSelector(workspaceSelector, (workspace) => !!workspace && workspace.analyses.length >= MAX_ENTITIES_COUNT);
export const maxResultSetsReachedSelector = createSelector(workspaceSelector, (workspace) => !!workspace && workspace.resultSets.length >= MAX_ENTITIES_COUNT);
export const maxConfigurationsReachedSelector = createSelector(selectedScenarioSelector, (scenario) => !!scenario.configurations && scenario.configurations.length >= MAX_ENTITIES_COUNT);
export const maxSimpleMetricsReachedSelector = createSelector(selectedScenarioSelector, (scenario) => !!scenario.absoluteRiskProfiles && scenario.absoluteRiskProfiles.length >= MAX_ENTITIES_COUNT);
export const maxRelativeMetricsReachedSelector = createSelector(selectedScenarioSelector, (scenario) => !!scenario.relativeRiskProfiles && scenario.relativeRiskProfiles.length >= MAX_ENTITIES_COUNT);
export const addEntityButtonDisabledSelector = createSelector(currentScenarioEntitySelector, maxConfigurationsReachedSelector, maxRelativeMetricsReachedSelector, maxSimpleMetricsReachedSelector, (currentScenarioEntity, maxConfigurationsReached, maxRelativeMetricsReached, maxSimpleMetricsReached) => {
    const maxConfigurations = currentScenarioEntity?.type === ScenarioEntityType.Configuration && maxConfigurationsReached;
    const maxRelativeMetrics = currentScenarioEntity?.type === ScenarioEntityType.RelativeMetric && maxRelativeMetricsReached;
    const maxSimpleMetrics = currentScenarioEntity?.type === ScenarioEntityType.SimpleMetric && maxSimpleMetricsReached;
    return [maxConfigurations, maxRelativeMetrics, maxSimpleMetrics].some(Boolean);
});
export const addEntityButtonDisabledMessageSelector = createSelector(currentScenarioEntitySelector, addEntityButtonDisabledSelector, (currentScenarioEntity, internalAddEntityButtonDefaultActionDisabled) => {
    if (!internalAddEntityButtonDefaultActionDisabled) {
        return undefined;
    }
    if (currentScenarioEntity?.type === ScenarioEntityType.Configuration) {
        return PSWorkspaceLabels.MAX_CONFIGURATIONS;
    }
    if (currentScenarioEntity?.type === ScenarioEntityType.SimpleMetric) {
        return PSWorkspaceLabels.MAX_SIMPLE_METRICS;
    }
    return PSWorkspaceLabels.MAX_RELATIVE_METRICS;
});
export const allResultSetsAreFullSelector = createSelector(workspaceSelector, (workspace) => workspace?.resultSets.every((resultSet) => {
    const count = getResultSetElementsCount(resultSet);
    return count >= MAX_ENTITIES_COUNT;
}));
export const notFullResultSetsSelector = createSelector(workspaceSelector, (workspace) => workspace?.resultSets.filter((resultSet) => {
    const count = getResultSetElementsCount(resultSet);
    return count < MAX_ENTITIES_COUNT;
}));
export const resultElementsConnectedToScenarioSelector = (entityType, scenarioId) => createSelector(workspaceResultSetsSelector, (resultSets) => {
    if (entityType !== PsGlobalEntityType.Scenario || !resultSets) {
        return [];
    }
    const resultElements = [];
    for (const resultSet of resultSets) {
        for (const element of resultSet.resultElements.tableElements) {
            if (element.configurations.find(({ analysisId }) => analysisId === scenarioId)) {
                resultElements.push(getResultElementSummaryData(element, resultSet));
            }
        }
        for (const element of resultSet.resultElements.lineGraphElements) {
            if (element.responses.find(({ analysisId }) => analysisId === scenarioId)) {
                resultElements.push(getResultElementSummaryData(element, resultSet));
            }
        }
        for (const element of resultSet.resultElements.heatMapElements) {
            if (element.responses.find(({ analysisId }) => analysisId === scenarioId)) {
                resultElements.push(getResultElementSummaryData(element, resultSet));
            }
        }
        for (const element of resultSet.resultElements.metadataElements) {
            if (element.configurations.find(({ analysisId }) => analysisId === scenarioId)) {
                resultElements.push(getResultElementSummaryData(element, resultSet));
            }
        }
    }
    return resultElements;
});
export const metricsConnectedToConfigurationSelector = (entityType, configurationId) => createSelector(selectedScenarioSelector, (scenario) => {
    const configuration = scenario.configurations && scenario.configurations.find(({ id }) => id === configurationId);
    if (entityType !== ScenarioEntityType.Configuration || !configuration) {
        return [];
    }
    const connectedMetrics = [];
    const getMetricData = (risk, metricType) => ({
        metricId: risk.riskId,
        metricType,
        metricHumanType: `${metricType === "SimpleMetric" ? "Simple" : "Relative"} metric`,
        metricName: risk.name,
    });
    configuration.attachedRelativeRisks.forEach((risk) => connectedMetrics.push(getMetricData(risk, "RelativeMetric")));
    configuration.attachedAbsoluteRisks.forEach((risk) => connectedMetrics.push(getMetricData(risk, "SimpleMetric")));
    return connectedMetrics;
});
export const resultElementsConnectedToConfigurationSelector = (entityType, configurationId) => createSelector(workspaceResultSetsSelector, selectedScenarioSelector, (resultSets, scenario) => {
    if (entityType !== ScenarioEntityType.Configuration || !resultSets) {
        return [];
    }
    const resultElements = [];
    for (const resultSet of resultSets) {
        for (const element of resultSet.resultElements.tableElements) {
            if (element.configurations.find((config) => config.configurationId === configurationId && config.analysisId === scenario.id)) {
                resultElements.push(getResultElementSummaryData(element, resultSet));
            }
        }
        for (const element of resultSet.resultElements.lineGraphElements) {
            if (element.responses.find((lineGraph) => lineGraph.analysisId === scenario.id &&
                (lineGraph.xConfigurationId === configurationId ||
                    lineGraph.yConfigurationId === configurationId))) {
                resultElements.push(getResultElementSummaryData(element, resultSet));
            }
        }
        for (const element of resultSet.resultElements.heatMapElements) {
            if (element.responses.find((heatMap) => heatMap.analysisId === scenario.id &&
                (heatMap.xConfigurationId === configurationId ||
                    heatMap.yConfigurationId === configurationId ||
                    heatMap.zConfigurationId === configurationId))) {
                resultElements.push(getResultElementSummaryData(element, resultSet));
            }
        }
        for (const element of resultSet.resultElements.metadataElements) {
            if (element.configurations.find((config) => config.configurationId === configurationId && config.analysisId === scenario.id)) {
                resultElements.push(getResultElementSummaryData(element, resultSet));
            }
        }
    }
    return resultElements;
});
export const configurationsConnectedToMetricSelector = (entityType, metricId) => createSelector(selectedScenarioSelector, (scenario) => {
    if (entityType === ScenarioEntityType.Configuration) {
        return [];
    }
    const connectedConfigurations = [];
    const getConfigurationSummaryData = (configuration) => ({
        id: configuration.id,
        name: configuration.name,
    });
    if (entityType === ScenarioEntityType.RelativeMetric) {
        !!scenario.attachedRelativeRisks &&
            scenario.attachedRelativeRisks
                .filter((metric) => metric.riskId === metricId)
                .forEach((metric) => {
                const { sourceId, targetId } = metric;
                const sourceConfiguration = scenario.configurations.find(({ id }) => id === sourceId);
                const targetConfiguration = scenario.configurations.find(({ id }) => id === targetId);
                if (sourceConfiguration) {
                    connectedConfigurations.push(getConfigurationSummaryData(sourceConfiguration));
                }
                if (targetConfiguration) {
                    connectedConfigurations.push(getConfigurationSummaryData(targetConfiguration));
                }
            });
    }
    else {
        !!scenario.attachedAbsoluteRisks &&
            scenario.attachedAbsoluteRisks
                .filter((metric) => metric.riskId === metricId)
                .forEach((metric) => {
                const targetConfiguration = scenario.configurations.find(({ id }) => id === metric.targetId);
                if (targetConfiguration) {
                    connectedConfigurations.push(getConfigurationSummaryData(targetConfiguration));
                }
            });
    }
    return connectedConfigurations;
});
export const shouldScrollToResultElementSelector = createSelector(getWorkspaceStateSelector, (workspace) => workspace.shouldScrollToResultElement);
